import { css, type SerializedStyles } from '@emotion/react';
import { theme } from 'global';

export const activationDashboardRoot = css`
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  align-items: center;
  height: 100%;
  min-height: fit-content;
  width: 100%;
  background-color: ${theme.colors.neutral[0]};
  padding: 20px 0 40px;

  @media screen and (min-width: ${theme.breaks.sm}px) {
    padding: 32px 0 32px 0;
  }
`;

export const activationDashboardContent = css`
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  gap: 20px;
  width: 100%;
  margin: 0 auto;
  padding: 0 16px;
  max-width: 1016px;

  @media screen and (min-width: ${theme.breaks.xs}px) {
    padding: 0 24px;
  }

  @media screen and (min-width: ${theme.breaks.sm}px) {
    gap: 32px;
    padding: 0 48px;
  }

  @media screen and (min-width: ${theme.breaks.md}px) {
    padding: 0 80px;
  }

  @media screen and (min-width: ${theme.breaks.lg}px) {
    width: calc(100% * 10 / 12);
    padding: 0;
  }
`;

export const welcomeHeading = css`
  color: ${theme.colors.secondary};
  margin: 0;
  font-size: 26px;
`;

export const card = css`
  background-color: ${theme.colors.white};
  border: 1px solid ${theme.colors.neutral[100]};
  border-radius: 12px;
  overflow: hidden;
  width: 100%;
`;

export const finaliseCard = css`
  ${card};
  display: flex;
  flex-direction: column;
`;

export const finaliseHeader = css`
  display: contents;

  @media screen and (min-width: ${theme.breaks.sm}px) {
    display: flex;
    align-items: center;
    gap: 8px;
    justify-content: space-between;
    padding: 24px 32px;
  }
`;

export const finaliseTitle = css`
  box-sizing: border-box;
  color: ${theme.colors.secondary};
  margin: 0;
  order: 0;
  padding: 20px;
  width: 100%;

  @media screen and (min-width: ${theme.breaks.sm}px) {
    order: initial;
    padding: 0;
    width: auto;
  }
`;

export const completionSummary = css`
  display: flex;
  align-items: center;
  flex-shrink: 0;
  gap: 8px;
  align-self: center;
  justify-content: center;
  order: 2;
  padding: 0 0 20px;

  @media screen and (min-width: ${theme.breaks.sm}px) {
    align-self: auto;
    justify-content: flex-start;
    order: initial;
    padding: 0;
  }
`;

export const completionLabel = css`
  color: ${theme.colors.neutral[500]};
  margin: 0;
  white-space: nowrap;
`;

export const progressRing = (progressValue: number): SerializedStyles => css`
  align-items: center;
  background: conic-gradient(
    ${theme.colors.primary.main} ${progressValue * 3.6}deg,
    ${theme.colors.neutral[200]} 0deg
  );
  border-radius: 50%;
  display: inline-flex;
  height: 30px;
  justify-content: center;
  position: relative;
  width: 30px;

  &::after {
    background-color: ${theme.colors.white};
    border-radius: 50%;
    content: '';
    height: 20px;
    width: 20px;
  }
`;

export const stepsContainer = css`
  display: flex;
  flex-direction: column;
  gap: 12px;
  order: 1;
  padding: 0 20px 12px;

  @media screen and (min-width: ${theme.breaks.sm}px) {
    order: initial;
    padding: 0 32px 32px;
  }
`;

export const stepRow = (isActive: boolean): SerializedStyles => css`
  align-items: ${isActive ? 'stretch' : 'flex-start'};
  background-color: ${isActive ? '#F9F7FF' : theme.colors.white};
  border: 1px solid
    ${isActive ? theme.colors.primary.main : theme.colors.neutral[100]};
  border-radius: 8px;
  display: flex;
  flex-direction: ${isActive ? 'column' : 'row'};
  flex-wrap: nowrap;
  gap: 20px;
  justify-content: ${isActive ? 'center' : 'flex-start'};
  min-width: 280px;
  padding: 20px;
  width: 100%;

  @media screen and (min-width: ${theme.breaks.sm}px) {
    align-items: center;
    flex-direction: row;
    flex-wrap: wrap;
    gap: 24px;
    padding: 24px;
  }
`;

export const stepContentGroup = (isActive: boolean): SerializedStyles => css`
  align-items: flex-start;
  display: flex;
  flex: ${isActive ? '1 0 0' : '1 1 auto'};
  gap: 8px;
  min-width: 0;
  width: ${isActive ? '100%' : 'auto'};

  @media screen and (min-width: ${theme.breaks.sm}px) {
    min-width: 200px;
    width: auto;
  }
`;

export const stepContent = css`
  display: flex;
  flex: 1;
  flex-direction: column;
  gap: 8px;
  justify-content: flex-start;
  min-width: 0;
`;

export const stepTitle = (status: string): SerializedStyles => css`
  color: ${status === 'completed'
    ? theme.colors.neutral[400]
    : theme.colors.secondary};
  margin: 0;
`;

export const stepDescription = css`
  color: ${theme.colors.neutral[600]};
  margin: 0;
`;

export const stepActions = (isActive: boolean): SerializedStyles => css`
  align-items: ${isActive ? 'stretch' : 'flex-start'};
  display: flex;
  flex-shrink: 0;
  justify-content: flex-end;
  width: ${isActive ? '100%' : 'auto'};

  @media screen and (min-width: ${theme.breaks.sm}px) {
    align-items: center;
    justify-content: flex-start;
    width: auto;
  }
`;

export const primaryAction = css`
  background: ${theme.colors.primary.dark};
  border: 0;
  border-radius: 8px;
  color: ${theme.colors.muted};
  cursor: pointer;
  min-height: 44px;
  padding: 14px 24px 15px;
  white-space: nowrap;

  &:hover,
  &:focus-visible {
    background: ${theme.colors.primary.dark};
  }

  &:focus-visible {
    outline: 2px solid ${theme.colors.primary.main};
    outline-offset: 2px;
  }

  @media screen and (min-width: ${theme.breaks.sm}px) {
    min-height: 0;
    padding: 11px 12px 12px;
    width: auto;
  }
`;

export const activePrimaryAction = css`
  width: 100%;

  @media screen and (min-width: ${theme.breaks.sm}px) {
    width: auto;
  }
`;

export const secondaryAction = css`
  background: transparent;
  border: 0;
  color: ${theme.colors.primary.medium};
  cursor: pointer;
  padding: 0;
`;

export const stepIndicator = (status: string): SerializedStyles => css`
  align-items: center;
  color: ${status === 'completed'
    ? theme.colors.success
    : status === 'active'
      ? theme.colors.primary.medium
      : theme.colors.neutral[600]};
  display: inline-flex;
  flex-shrink: 0;
  height: 24px;
  justify-content: center;
  width: 24px;

  svg {
    display: block;
    height: 24px;
    width: 24px;
  }
`;

export const numberIndicator = css`
  align-items: center;
  border: 2px solid currentColor;
  border-radius: 50%;
  display: inline-flex;
  height: 20px;
  justify-content: center;
  width: 20px;
`;

export const durationLabel = css`
  && {
    align-items: center;
    background-color: ${theme.colors.white};
    border: 1px solid ${theme.colors.neutral[200]};
    border-radius: 4px;
    color: ${theme.colors.neutral[500]};
    cursor: default;
    display: inline-flex;
    height: auto;
    min-height: 0;
    padding: 2px 8px;
    width: fit-content;
  }

  && .MuiChip-label {
    padding: 0;
  }
`;

export const faqCard = css`
  ${card};
  padding: 20px;
  border: 1px solid ${theme.colors.neutral[100]};

  @media screen and (min-width: ${theme.breaks.md}px) {
    padding: 24px;
  }
`;

export const faqContent = css`
  align-items: flex-start;
  display: flex;
  flex-direction: column;
  gap: 16px;
  width: 100%;

  @media screen and (min-width: ${theme.breaks.xs}px) {
    gap: 20px;
  }

  @media screen and (min-width: ${theme.breaks.md}px) {
    flex-direction: row;
    gap: 24px;
  }
`;

export const faqIntro = css`
  display: flex;
  flex: none;
  flex-direction: column;
  gap: 8px;
  justify-content: center;
  max-width: none;
  min-width: 0;
  width: 100%;

  @media screen and (min-width: ${theme.breaks.md}px) {
    flex: 1 0 0;
    gap: 20px;
    max-width: 320px;
    min-width: 280px;
    width: auto;
  }
`;

export const faqTitle = css`
  color: ${theme.colors.secondary};
  margin: 0;
`;

export const faqBody = css`
  color: ${theme.colors.neutral[600]};
  margin: 0;
`;

export const faqLink = css`
  color: ${theme.colors.primary.medium};
  text-decoration: underline;
`;

export const faqList = css`
  display: flex;
  flex: none;
  flex-direction: column;
  min-width: 0;
  width: 100%;

  @media screen and (min-width: ${theme.breaks.md}px) {
    flex: 1 1 0;
    width: auto;
  }
`;

export const faqItem = (
  hasDivider: boolean,
  isExpanded: boolean
): SerializedStyles => css`
  border-bottom: ${hasDivider || isExpanded
    ? `1px solid ${
        isExpanded ? theme.colors.primary.medium : theme.colors.neutral[50]
      }`
    : '0'};
  width: 100%;
`;

export const faqQuestion = (isExpanded: boolean): SerializedStyles => css`
  align-items: center;
  background: transparent;
  border: 0;
  color: ${theme.colors.secondary};
  cursor: pointer;
  display: flex;
  gap: 24px;
  justify-content: space-between;
  padding: 20px 0 ${isExpanded ? '16px' : '20px'};
  text-align: left;
  width: 100%;

  &:focus-visible {
    outline: 2px solid ${theme.colors.primary.main};
    outline-offset: 2px;
  }
`;

export const faqQuestionText = css`
  color: ${theme.colors.secondary};
  flex: 1;
`;

export const faqChevron = css`
  color: ${theme.colors.primary.medium};
  flex-shrink: 0;
  height: 24px;
  width: 24px;
`;

export const faqAnswer = css`
  color: ${theme.colors.neutral[600]};
  padding: 0 0 20px;

  p {
    margin: 0 0 16px;
  }

  p:has(strong) {
    margin-bottom: 0;
  }

  p:last-child,
  ul:last-child {
    margin-bottom: 0;
  }

  ul {
    list-style-type: disc;
    margin: 0 0 16px;
    padding-left: 18px;
  }

  ul ul {
    list-style-type: disc;
    margin: 8px 0 0;
    padding-left: 18px;
  }

  a {
    color: ${theme.colors.primary.medium};
    text-decoration: underline;
  }

  @media screen and (min-width: ${theme.breaks.xs}px) {
    padding: 0 48px 20px 0;
  }
`;

export const activationStepContainer = css`
  background-color: ${theme.colors.white};
  border-radius: ${theme.radii.lg};
  margin: 0 auto;
  max-width: 600px;
  padding: 16px;
  width: 100%;

  @media screen and (min-width: ${theme.breaks.sm}px) {
    border-radius: 12px;
    padding: 32px;
  }

  @media screen and (max-width: ${theme.breaks.xs}px) {
    padding-bottom: 76px;
  }
`;

export const activationStepPlaceholder = css`
  color: ${theme.colors.secondary};
  margin: 0;
`;
